import { FormInstance } from "antd";
import { QueryOperation, SearchQuery } from "../code/search-query/search-query";
import {
  buildSearchQuery,
  comboboxValue,
  dateValue,
  QueryFieldMapping,
} from "../code/search-query/query-mapper";
import { DataColumn } from "../controls/Data.default";
import { DynamicSchema } from "../controls/DynamicForm.default";
import { InputObject } from "../controls/default";
import {
  createScopedFilterRequest,
  createSearchRequest,
} from "../hooks/useSearchRequest";
import {
  getCompanies,
  getCompanyAssets,
  getCompanyDepartments,
  getCompanyUserRoles,
} from "../repository/data.repository";

export interface ManagersViewForm {
  readonly userName: string;
  readonly firstName: string;
  readonly middleName: string;
  readonly lastName: string;
  readonly dateOfBirth: string;
  readonly email: string;
  readonly emailConfirmed: boolean;
  readonly phoneNumber: string;
  readonly companyPhoneNumber: string;
  readonly company?: InputObject;
  readonly department?: InputObject;
  readonly role?: InputObject;
  readonly asset?: InputObject;
}

export const managerViewColumns: DataColumn[] = [
  {
    title: "Company",
    key: "companyName",
    width: 192,
    sort: true,
    defaultSort: "asc",
    fixed: "left",
  },
  // §4.4 says managers should be filterable by company, department, vessel/site and role; only company
  // was built, and the grid did not show the other three either — even though All has shown Department
  // all along and every one of the 51 managers has one. Columns first, filters in the next unit: a filter
  // over a column nobody can see cannot be trusted by the person using it.
  {
    title: "Department",
    key: "departmentName",
    width: 192,
    sort: true,
  },
  {
    title: "Role",
    key: "roleName",
    width: 160,
    sort: true,
  },
  // Empty for every seeded manager today, and that is correct rather than broken: the manager wizard
  // offers an asset but does not require one. The column exists so the §4.4 filter has something visible
  // to act on, and so a manager who IS mapped to a vessel or site can be seen to be.
  {
    title: "Asset",
    key: "assetName",
    width: 192,
    sort: true,
  },
  {
    title: "User Name",
    key: "userName",
    width: 192,
    sort: true,
  },
  {
    title: "First Name",
    key: "firstName",
    width: 192,
    sort: true,
  },
  {
    title: "Middle Name",
    key: "middleName",
    width: 192,
    sort: true,
  },
  {
    title: "Last Name",
    key: "lastName",
    width: 192,
    sort: true,
  },
  {
    title: "Date Of Birth",
    key: "dateOfBirth",
    width: 128,
    format: "DD/MM/YYYY",
    sort: true,
  },
  {
    title: "Email",
    key: "email",
    width: 256,
    sort: true,
  },
  {
    title: "Email Confirmed",
    key: "emailConfirmed",
    width: 128,
    sort: true,
  },
  {
    title: "Phone",
    key: "phoneNumber",
    width: 192,
    sort: true,
  },
  {
    title: "Company Phone",
    key: "companyPhoneNumber",
    width: 192,
    sort: true,
  },
  // §4.3 lists "last login" among the All-grid fields. A real column rather than a derived
  // MAX(LoginHistory.Timestamp), so sorting on it stays cheap. Blank until the person signs in again:
  // the field was renamed from a dead LastOnline and deliberately NOT back-filled, because 902 of the
  // 2 086 seeded login-history rows are dated in the FUTURE and a back-fill would import that.
  {
    title: "Last Login",
    key: "lastLoginAt",
    width: 144,
    format: "DD/MM/YYYY HH:mm",
    sort: true,
  },
  // Admin requirement 2026-11-03.
  {
    title: "Status",
    key: "isActive",
    width: 110,
    sort: true,
    transform: (v: boolean) => (v === false ? "Inactive" : "Active"),
  },
  {
    title: "Activated On",
    key: "activatedAt",
    width: 148,
    format: "DD/MM/YYYY",
    sort: true,
  },
  {
    title: "Deactivated On",
    key: "deactivatedAt",
    width: 148,
    format: "DD/MM/YYYY",
    sort: true,
  },
];

const managersQueryMappings: QueryFieldMapping[] = [
  { formField: "company", queryField: "companyUid", operation: QueryOperation.Equal, transform: comboboxValue },
  // §4.4 is explicit for this tab: "Filters should include company, department, vessel/site, and role."
  // Company was the only one of the four that existed.
  { formField: "department", queryField: "departmentUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "role", queryField: "roleUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "asset", queryField: "assetUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "userName" },
  { formField: "firstName" },
  { formField: "middleName" },
  { formField: "lastName" },
  { formField: "dateOfBirth", operation: QueryOperation.Equal, transform: dateValue },
  { formField: "email" },
  { formField: "emailConfirmed", operation: QueryOperation.Equal },
  { formField: "phoneNumber" },
  { formField: "companyPhoneNumber" },
];

export const mapManagersViewQuery = (values: ManagersViewForm): SearchQuery =>
  buildSearchQuery(values, managersQueryMappings);

// A factory rather than a constant — Department / Role / Asset scope their options to the chosen Company.
// See the note on the All tab: `dependsOn` is a cascade reset, not a visibility rule, so all three work
// with no company selected and are cleared when the company changes.
export const managersSearchSchema = (form: FormInstance<any>): DynamicSchema => ({
  groups: [
    {
      controls: [
        {
          type: "combobox",
          name: "company",
          label: "Company",
          request: createSearchRequest(getCompanies, "name"),
        },
        {
          type: "combobox",
          name: "department",
          label: "Department",
          dependsOn: ["company"],
          request: createScopedFilterRequest(getCompanyDepartments, "name", () => [
            ["companyUid", form.getFieldValue("company")?.value],
          ], undefined, { subDisplayField: "companyName" }),
        },
        {
          type: "combobox",
          name: "role",
          label: "Role",
          dependsOn: ["company"],
          request: createScopedFilterRequest(getCompanyUserRoles, "name", () => [
            ["companyUid", form.getFieldValue("company")?.value],
          ], undefined, { subDisplayField: "companyName" }),
        },
        // The Asset column on this tab is empty for every seeded manager, and this filter is why it exists:
        // §4.4 asks for it, the wizard can set one, and a filter over an invisible column is untrustworthy.
        {
          type: "combobox",
          name: "asset",
          label: "Asset",
          dependsOn: ["company"],
          request: createScopedFilterRequest(getCompanyAssets, "name", () => [
            ["companyUid", form.getFieldValue("company")?.value],
          ], undefined, { subDisplayField: "companyName" }),
        },
        { type: "textbox", name: "userName", label: "User Name" },
        { type: "textbox", name: "firstName", label: "First Name" },
        { type: "textbox", name: "middleName", label: "Middle Name" },
        { type: "textbox", name: "lastName", label: "Last Name" },
        { type: "datepicker", name: "dateOfBirth", label: "Date Of Birth" },
        { type: "textbox", name: "email", label: "Email" },
        { type: "togglebox", name: "emailConfirmed", label: "Email Confirmed" },
        { type: "textbox", name: "phoneNumber", label: "Phone" },
        { type: "textbox", name: "companyPhoneNumber", label: "Company Phone" },
      ],
    },
  ],
});
